import { useEffect, useMemo, useState } from "react";
import { getOrdersWaitingPayment, paidOrder } from "../../api/orderBoard";
import styles from "./main.module.css";

const USE_MOCK = false;

const MOCK_WAITING_ORDERS = [
  {
    id: 101,
    receiver: "Иван Петров",
    phone: "+7 (999) 111-22-33",
    address: "г. Москва, ул. Ленина, д. 15, офис 4",
    dateCreated: "2026-04-10T09:30:00",
    clientId: 12,
    statusFinance: 0,
    columnId: 3,
    price: 12500,
    wayDelivery: 1,
    deliveryDescription: "Доставить до двери, позвонить за час",
    customerType: "Физическое лицо",
    paymentUrl: "https://example.com/pay/101",
  },
  {
    id: 102,
    receiver: "ООО Альфа",
    phone: "+7 (999) 222-33-44",
    address: "г. Санкт-Петербург, Невский проспект, д. 21",
    dateCreated: "2026-04-09T14:10:00",
    clientId: 24,
    statusFinance: 0,
    columnId: 5,
    price: 34800,
    wayDelivery: 0,
    deliveryDescription: "Самовывоз со склада",
    customerType: "Юридическое лицо",
    paymentUrl: "https://example.com/pay/102",
  },
  {
    id: 103,
    receiver: "Анна Смирнова",
    phone: "+7 (999) 333-44-55",
    address: "г. Казань, ул. Баумана, д. 8",
    dateCreated: "2026-04-08T18:45:00",
    clientId: null,
    statusFinance: 0,
    columnId: null,
    price: 7900,
    wayDelivery: 1,
    deliveryDescription: null,
    customerType: "Физическое лицо",
    paymentUrl: "https://example.com/pay/103",
  },
];

export default function Main({ jwt }) {
  const [waiting, setWaiting] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [payingId, setPayingId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError("");

        if (USE_MOCK) {
          await new Promise((resolve) => setTimeout(resolve, 300));

          if (!cancelled) {
            setWaiting(MOCK_WAITING_ORDERS);
          }

          return;
        }

        const res = await getOrdersWaitingPayment(jwt);

        if (!res.ok) {
          throw new Error(`Ошибка загрузки: ${res.status}`);
        }

        const data = await res.json();

        if (!cancelled) {
          setWaiting(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Не удалось загрузить заказы");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [jwt]);

  const selectedOrder = useMemo(() => {
    if (!selected) return null;
    return waiting.find((x) => x.id === selected.id) || selected;
  }, [selected, waiting]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const formatPrice = (value) => {
    if (value == null) return "—";
    return `${new Intl.NumberFormat("ru-RU").format(Number(value))} ₽`;
  };

  const getFinanceLabel = (statusFinance) => {
    switch (statusFinance) {
      case 0:
        return "Не оплачено";
      case 1:
        return "Оплачено";
      default:
        return `Статус: ${statusFinance}`;
    }
  };

  const getDeliveryLabel = (wayDelivery) => {
    switch (wayDelivery) {
      case 0:
        return "Самовывоз";
      case 1:
        return "Доставка";
      default:
        return `Способ: ${wayDelivery}`;
    }
  };

  const handlePaid = async (id) => {
    try {
      setPayingId(id);

      if (USE_MOCK) {
        await new Promise((resolve) => setTimeout(resolve, 400));
      } else {
        const res = await paidOrder(jwt, id);

        if (!res.ok && res.status !== 204) {
          throw new Error(`Ошибка оплаты: ${res.status}`);
        }
      }

      setWaiting((prev) => prev.filter((x) => x.id !== id));
      setSelected((prev) => (prev?.id === id ? null : prev));
    } catch (err) {
      alert(err.message || "Не удалось отметить заказ как оплаченный");
    } finally {
      setPayingId(null);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>
            Ожидают оплату {USE_MOCK ? "(mock)" : ""}
          </h1>
          <p className={styles.subtitle}>
            Заказы, по которым нужно проверить и подтвердить оплату
          </p>
        </div>

        <div className={styles.counter}>{waiting.length}</div>
      </div>

      {loading && <div className={styles.emptyBlock}>Загрузка заказов...</div>}

      {!loading && error && <div className={styles.errorBlock}>{error}</div>}

      {!loading && !error && waiting.length === 0 && (
        <div className={styles.emptyBlock}>
          Заказов, ожидающих оплату, нет
        </div>
      )}

      {!loading && !error && waiting.length > 0 && (
        <div className={styles.items}>
          {waiting.map((order) => (
            <div key={order.id} className={styles.order}>
              <button
                className={styles.openButton}
                onClick={() => setSelected(order)}
                type="button"
              >
                <div className={styles.topRow}>
                  <div className={styles.orderId}>Заказ #{order.id}</div>
                  <div className={styles.price}>{formatPrice(order.price)}</div>
                </div>

                <div className={styles.receiver}>{order.receiver}</div>

                <div className={styles.bottomRow}>
                  <div className={styles.badge}>
                    {getFinanceLabel(order.statusFinance)}
                  </div>

                  <div className={styles.badgeMuted}>
                    {getDeliveryLabel(order.wayDelivery)}
                  </div>

                  <div className={styles.badgeMuted}>
                    {formatDate(order.dateCreated)}
                  </div>

                  {order.customerType && (
                    <div className={styles.badgeMuted}>{order.customerType}</div>
                  )}
                </div>
              </button>
            </div>
          ))}
        </div>
      )}

      <div
        className={`${styles.overlay} ${
          selectedOrder ? styles.overlayActive : ""
        }`}
        onClick={() => setSelected(null)}
      />

      <div
        className={`${styles.drawer} ${
          selectedOrder ? styles.drawerActive : ""
        }`}
      >
        {selectedOrder && (
          <>
            <div className={styles.drawerHeader}>
              <div className={styles.drawerHeaderMain}>
                <div className={styles.drawerId}>Заказ #{selectedOrder.id}</div>
                <div className={styles.drawerDate}>
                  Создан: {formatDate(selectedOrder.dateCreated)}
                </div>
              </div>

              <div className={styles.drawerActions}>
                <button
                  className={styles.closeOrderButton}
                  type="button"
                  disabled={payingId === selectedOrder.id}
                  onClick={() => handlePaid(selectedOrder.id)}
                >
                  {payingId === selectedOrder.id ? "Сохраняем..." : "Оплачено"}
                </button>

                <button
                  className={styles.drawerCloseButton}
                  type="button"
                  onClick={() => setSelected(null)}
                >
                  ×
                </button>
              </div>
            </div>

            <div className={styles.drawerBody}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Получатель</div>
                  <div className={styles.fieldValue}>
                    {selectedOrder.receiver || "—"}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Телефон</div>
                  <div className={styles.fieldValue}>
                    {selectedOrder.phone || "—"}
                  </div>
                </div>

                <div className={styles.formGroupFull}>
                  <div className={styles.fieldLabel}>Адрес</div>
                  <div className={styles.fieldValueMultiline}>
                    {selectedOrder.address || "—"}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Сумма</div>
                  <div className={styles.fieldValue}>
                    {formatPrice(selectedOrder.price)}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Финансовый статус</div>
                  <div className={styles.fieldValue}>
                    {getFinanceLabel(selectedOrder.statusFinance)}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Способ доставки</div>
                  <div className={styles.fieldValue}>
                    {getDeliveryLabel(selectedOrder.wayDelivery)}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>Тип клиента</div>
                  <div className={styles.fieldValue}>
                    {selectedOrder.customerType || "—"}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>ClientId</div>
                  <div className={styles.fieldValue}>
                    {selectedOrder.clientId ?? "—"}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <div className={styles.fieldLabel}>ColumnId</div>
                  <div className={styles.fieldValue}>
                    {selectedOrder.columnId ?? "—"}
                  </div>
                </div>

                <div className={styles.formGroupFull}>
                  <div className={styles.fieldLabel}>Описание доставки</div>
                  <div className={styles.fieldValueMultiline}>
                    {selectedOrder.deliveryDescription || "—"}
                  </div>
                </div>

                <div className={styles.formGroupFull}>
                  <div className={styles.fieldLabel}>Ссылка на оплату</div>
                  <div className={styles.fieldValueMultiline}>
                    {selectedOrder.paymentUrl ? (
                      <a
                        href={selectedOrder.paymentUrl}
                        target="_blank"
                        rel="noreferrer"
                        className={styles.link}
                      >
                        {selectedOrder.paymentUrl}
                      </a>
                    ) : (
                      "—"
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}